import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Users, Briefcase, Loader2, Check, MessageCircle, Info, X, Star, Clock, Navigation, Timer, MapPin } from "lucide-react";
import { CLASS_IMAGES, formatGBP, createBooking } from "../../lib/booking";

const SHORT_NAMES = { business: "E Class", executive: "S Class", mpv: "V Class", suv: "Range Rover" };
const POPULAR_KEY = "executive";

const fmtWhen = (dt) => {
  if (!dt) return "";
  const d = new Date(dt);
  if (isNaN(d.getTime())) return dt;
  return d.toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};
const fmtDuration = (m) => {
  if (!m) return null;
  const h = Math.floor(m / 60), mm = m % 60;
  return h ? `${h}h ${mm}m` : `${mm} min`;
};

const ClassCard = ({ q, onReserve, busy, bookedRef, waUrl }) => {
  const isPopular = q.class_key === POPULAR_KEY;
  return (
    <div
      className={`relative overflow-hidden rounded-xl border bg-white/[0.03] ${isPopular ? "border-gold ring-1 ring-gold/50" : "border-white/10"}`}
      data-testid={`quote-card-${q.class_key}`}
    >
      <div className="flex gap-3 p-2.5 sm:gap-5 sm:p-4">
        <div className="relative h-20 w-32 shrink-0 overflow-hidden rounded-lg bg-black sm:h-36 sm:w-60">
          <img src={CLASS_IMAGES[q.class_key]} alt={SHORT_NAMES[q.class_key] || q.model} className="h-full w-full object-cover" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col justify-between">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="font-heading text-lg text-white sm:text-2xl">{SHORT_NAMES[q.class_key] || q.model}</p>
              <p className="text-[0.58rem] uppercase tracking-[0.15em] text-gold/80 sm:text-[0.65rem]">{q.class_name}</p>
              <div className="mt-1 flex items-center gap-3 text-[0.65rem] text-white/50 sm:mt-2 sm:text-sm">
                <span className="flex items-center gap-1"><Users size={12} className="text-gold/70" />{q.seats}</span>
                <span className="flex items-center gap-1"><Briefcase size={12} className="text-gold/70" />{q.luggage}</span>
              </div>
            </div>
            <div className="shrink-0 text-right">
              <p className="font-heading text-lg leading-none text-gold sm:text-3xl" data-testid={`quote-total-${q.class_key}`}>
                {formatGBP(q.total)}
              </p>
              <p className="mt-0.5 text-[0.53rem] uppercase tracking-wider text-white/40 sm:text-[0.6rem]">incl. taxes</p>
            </div>
          </div>
          <div className="mt-2 flex items-center justify-end gap-2 sm:mt-3">
            {bookedRef ? (
              <>
                <span className="mr-auto flex items-center gap-1 text-[0.65rem] text-emerald-400" data-testid={`quote-ref-${q.class_key}`}>
                  <Check size={12} /> {bookedRef}
                </span>
                <a href={waUrl} target="_blank" rel="noopener noreferrer" data-testid={`quote-open-wa-${q.class_key}`}
                  className="inline-flex items-center gap-1.5 rounded-md bg-[#25D366] px-3 py-1.5 text-[0.7rem] font-medium text-white">
                  <MessageCircle size={13} /> WhatsApp
                </a>
              </>
            ) : (
              <button type="button" onClick={() => onReserve(q.class_key)} disabled={busy} data-testid={`quote-reserve-${q.class_key}`}
                className="inline-flex items-center gap-1.5 rounded-md bg-gold px-4 py-1.5 text-[0.72rem] font-medium uppercase tracking-wider text-ink transition-colors hover:bg-gold-light disabled:opacity-60 sm:px-5 sm:py-2 sm:text-sm">
                {busy ? <Loader2 size={13} className="animate-spin" /> : "Reserve"}
              </button>
            )}
          </div>
        </div>
      </div>
      {isPopular && (
        <div className="flex items-center justify-center gap-1.5 border-t border-gold/30 bg-gold/[0.08] py-1.5 text-[0.6rem] font-semibold uppercase tracking-[0.22em] text-gold" data-testid={`quote-popular-${q.class_key}`}>
          <Star size={11} className="fill-gold text-gold" /> Most Popular
        </div>
      )}
    </div>
  );
};

export const QuoteResults = ({ open, onOpenChange, results, quoteReq }) => {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState({ name: "", phone: "" });
  const [busyKey, setBusyKey] = useState(null);
  const [booked, setBooked] = useState(null);

  useEffect(() => {
    if (open) {
      setBooked(null);
      setBusyKey(null);
    }
    const l = window.__lenis;
    if (open) {
      document.body.style.overflow = "hidden";
      if (l) l.stop();
    } else {
      document.body.style.overflow = "";
      if (l) l.start();
    }
    return () => {
      document.body.style.overflow = "";
      if (window.__lenis) window.__lenis.start();
    };
  }, [open]);

  useEffect(() => {
    const h = (e) => e.key === "Escape" && onOpenChange(false);
    if (open) document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [open, onOpenChange]);

  const reserve = async (key) => {
    if (!customer.name.trim()) {
      toast.error("Please enter your name to reserve.");
      return;
    }
    if (customer.phone.replace(/\D/g, "").length < 7) {
      toast.error("Please enter a valid phone number.");
      return;
    }
    setBusyKey(key);
    try {
      const data = await createBooking({ quote_request: quoteReq, selected_class: key, customer });
      toast.success(`Booking ${data.ref} created — redirecting to secure payment…`);
      onOpenChange(false);
      navigate(`/pay/${data.ref}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create booking. Please try again.");
    } finally {
      setBusyKey(null);
    }
  };

  if (!open || !results) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100]" data-testid="quote-results">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={() => onOpenChange(false)} />
      <div className="absolute inset-0 flex sm:items-center sm:justify-center sm:p-6">
        <div className="relative flex h-full w-full flex-col overflow-hidden bg-charcoal text-white shadow-2xl sm:h-auto sm:max-h-[88vh] sm:max-w-3xl sm:rounded-2xl">
          {/* Header: centered title + highlighted journey summary */}
          <div className="relative shrink-0 border-b border-white/10 px-5 py-4 sm:px-7 sm:py-5">
            <button type="button" onClick={() => onOpenChange(false)} data-testid="quote-close"
              className="absolute right-3 top-3 rounded-full p-2 text-white/60 transition-colors hover:bg-white/10 hover:text-white">
              <X size={22} />
            </button>
            <h2 className="text-center font-heading text-xl font-light text-white sm:text-2xl">
              Choose your <span className="italic text-gold">Class</span>
            </h2>

            <div className="mx-auto mt-3.5 max-w-xl space-y-2 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3">
              <div className="flex items-start gap-2.5" data-testid="quote-summary-pickup">
                <MapPin size={14} className="mt-0.5 shrink-0 text-gold" />
                <div className="min-w-0">
                  <p className="text-[0.56rem] uppercase tracking-[0.18em] text-white/45">Pickup</p>
                  <p className="text-sm font-medium leading-snug text-white">{quoteReq?.pickup?.description || "—"}</p>
                </div>
              </div>
              {quoteReq?.dropoff?.description && (
                <div className="flex items-start gap-2.5" data-testid="quote-summary-dropoff">
                  <Navigation size={14} className="mt-0.5 shrink-0 text-gold" />
                  <div className="min-w-0">
                    <p className="text-[0.56rem] uppercase tracking-[0.18em] text-white/45">Drop-off</p>
                    <p className="text-sm font-medium leading-snug text-white">{quoteReq.dropoff.description}</p>
                  </div>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 border-t border-white/10 pt-2.5 text-xs" data-testid="quote-summary-meta">
                <span className="flex items-center gap-1.5 font-medium text-white/90"><Clock size={13} className="text-gold" />{fmtWhen(quoteReq?.datetime)}</span>
                {results.distance_miles ? (
                  <span className="flex items-center gap-1.5 font-medium text-white/90"><Navigation size={13} className="text-gold" />approx. {results.distance_miles} mi</span>
                ) : null}
                {fmtDuration(results.duration_minutes) ? (
                  <span className="flex items-center gap-1.5 font-medium text-white/90"><Timer size={13} className="text-gold" />~{fmtDuration(results.duration_minutes)} drive</span>
                ) : null}
              </div>
            </div>
          </div>

          {/* Scrollable body — data-lenis-prevent lets native touch/wheel scroll work inside the modal */}
          <div data-lenis-prevent className="min-h-0 flex-1 touch-pan-y overflow-y-auto overscroll-contain px-5 py-4 sm:px-7 sm:py-6"
            style={{ WebkitOverflowScrolling: "touch" }}>
            <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <input value={customer.name} onChange={(e) => setCustomer((c) => ({ ...c, name: e.target.value }))}
                placeholder="Full name *" data-testid="quote-customer-name"
                className="w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-white/35 focus:border-gold/60 focus:outline-none" />
              <input value={customer.phone} onChange={(e) => setCustomer((c) => ({ ...c, phone: e.target.value }))}
                placeholder="Phone number *" inputMode="tel" data-testid="quote-customer-phone"
                className="w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-white/35 focus:border-gold/60 focus:outline-none" />
            </div>
            <p className="mb-3 flex items-start gap-1.5 text-[0.68rem] leading-relaxed text-white/40">
              <Info size={12} className="mt-0.5 shrink-0 text-gold/70" />
              Indicative price incl. VAT — you'll continue to secure card payment. * Name &amp; phone required.
            </p>

            <div className="grid grid-cols-1 gap-3">
              {results.quotes.map((q) => (
                <ClassCard key={q.class_key} q={q} onReserve={reserve} busy={busyKey === q.class_key}
                  bookedRef={booked?.key === q.class_key ? booked.ref : null}
                  waUrl={booked?.key === q.class_key ? booked.waUrl : null} />
              ))}
            </div>
            <div className="h-20 sm:h-6" aria-hidden data-testid="quote-bottom-spacer" />
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
